import { Box } from "@mui/material";
import { type ChangeEvent, type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import type { DepartmentLocation } from "../../utils/models";
import { useUpdateDepartmentLocations } from "../Settings/DepartmentConfiguration/forms/hooks";

interface EditFacilityPageAddressProps {
	location: DepartmentLocation;
	onSaveSuccess?: () => void;
}

export const EditFacilityPageAddress: FC<EditFacilityPageAddressProps> = ({ location, onSaveSuccess }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { setChosenDepartment, showErrorSnackbar } = useHospitalData();
	const [address, setAddress] = useState("");

	useEffect(() => {
		setAddress(location.address ?? "");
	}, [location]);

	const { mutateAsync: updateDepartmentLocations, isPending: isUpdating } = useUpdateDepartmentLocations({
		existingDepartmentLocations: [{ ...location, address }],
		newDepartmentLocations: [],
	});

	const handleAddressChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setAddress(e.target.value);
	}, []);

	const handleSave = useCallback(async () => {
		try {
			const updatedLocations = await updateDepartmentLocations();
			if (updatedLocations && updatedLocations.length > 0) {
				setChosenDepartment((prev) => ({
					...prev,
					locations: prev.locations.map((loc) => (loc.id === location.id ? { ...loc, address } : loc)),
				}));
				showSnackbar(t("facility.address.updated.success", { defaultValue: "Address updated successfully" }), "success");
				onSaveSuccess?.();
			}
		} catch (error) {
			showErrorSnackbar(
				t("facility.address.updated.error", { defaultValue: "Failed to update address" }),
				{
					location_id: location.id,
					address,
				},
				error,
			);
		}
	}, [updateDepartmentLocations, setChosenDepartment, location.id, address, showSnackbar, t, onSaveSuccess, showErrorSnackbar]);

	return (
		<Box
			key={location.id}
			sx={{
				display: "flex",
				gap: 1,
				alignItems: "center",
			}}
		>
			<SystemValidationTextField
				label={t("facility.address.label", { defaultValue: "Address" })}
				value={address}
				onChange={handleAddressChange}
				placeholder={t("facility.address.placeholder", { defaultValue: "Enter facility address" })}
				disabled={isUpdating}
				reserveHeightForHelperText={false}
				inputSx={{
					width: "200px",
				}}
			/>
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
				}}
			>
				<SystemButton
					loading={isUpdating}
					tooltip={"Ensure that the address is different than the initial one, and is not empty."}
					buttonVariant={"secondary"}
					onClick={handleSave}
					disabled={isUpdating || address.trim() === location.address?.trim() || address.trim() === ""}
					sx={{
						p: 0,
						height: "fit-content",
						width: "fit-content",
						minWidth: "80px",
					}}
				>
					{t("common.save", { defaultValue: "Save" })}
				</SystemButton>
			</Box>
		</Box>
	);
};
